import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { fetchData } from '../../helpers/apiUtils';
import { FollowupCampaign, FollowupSegmentStatus } from '../../helpers/webinarMailingData';
import moment from 'moment';

interface FollowupCampaignListProps {
  onEdit: (campaignId: number) => void;
  onNew: () => void;
}

const FollowupCampaignList: React.FC<FollowupCampaignListProps> = ({ onEdit, onNew }) => {
  const [campaigns, setCampaigns] = useState<FollowupCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<FollowupCampaign | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState<number | null>(null);

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchData<FollowupCampaign[]>(
        '/api/webinar-followups/campaigns?limit=100',
      );
      if (response.success && response.data) {
        setCampaigns(response.data);
      } else {
        setError('Fehler beim Laden der Kampagnen');
      }
    } catch (err) {
      setError('Fehler beim Laden der Kampagnen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const handleDelete = async (campaignId: number) => {
    if (!window.confirm('Nachfass-Kampagne wirklich löschen?')) return;

    try {
      const response = await fetchData(`/api/webinar-followups/campaigns/${campaignId}`, {
        method: 'DELETE',
      });
      if (response.success) {
        loadCampaigns();
      } else {
        alert('Fehler beim Löschen: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler beim Löschen');
      console.error(err);
    }
  };

  const handleCancelClick = (campaign: FollowupCampaign) => {
    setCancelTarget(campaign);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;

    setCancelling(true);
    try {
      const response = await fetchData<{
        campaign_id: number;
        campaign_status: string;
        total_cancelled: number;
        total_already_sent: number;
      }>(`/api/webinar-followups/campaigns/${cancelTarget.id}/cancel`, {
        method: 'POST',
      });

      if (response.success && response.data) {
        const { total_cancelled, total_already_sent } = response.data;
        let message = `Kampagne abgebrochen: ${total_cancelled} E-Mail(s) storniert`;
        if (total_already_sent > 0) {
          message += `, ${total_already_sent} bereits gesendet (nicht stornierbar)`;
        }
        alert(message);
        loadCampaigns();
      } else {
        alert('Fehler beim Abbrechen: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler beim Abbrechen');
      console.error(err);
    } finally {
      setCancelling(false);
      setCancelDialogOpen(false);
      setCancelTarget(null);
    }
  };

  const handleCancelClose = () => {
    setCancelDialogOpen(false);
    setCancelTarget(null);
  };

  const handleReschedule = async (campaignId: number) => {
    setRescheduling(campaignId);
    try {
      const response = await fetchData<{
        campaign_id: number;
        new_status: string;
      }>(`/api/webinar-followups/campaigns/${campaignId}/reschedule`, {
        method: 'POST',
      });

      if (response.success) {
        // Reload list to show updated status
        loadCampaigns();
        // Navigate to editor to edit the campaign
        onEdit(campaignId);
      } else {
        alert('Fehler beim Freigeben: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler beim Freigeben');
      console.error(err);
    } finally {
      setRescheduling(null);
    }
  };

  const getSegmentStatusChip = (
    status: FollowupSegmentStatus | undefined,
    enabled: boolean | undefined,
    recipients: number | undefined,
    label: string,
  ) => {
    // If segment is disabled
    if (enabled === false) {
      return (
        <Tooltip title={`${label} deaktiviert`}>
          <Chip
            size="small"
            label="Aus"
            sx={{ backgroundColor: '#9e9e9e', color: 'white', minWidth: 60 }}
          />
        </Tooltip>
      );
    }

    const colors: Record<FollowupSegmentStatus, string> = {
      draft: '#757575',
      awaiting_webinar: '#ff9800',
      scheduled: '#1976d2',
      sent: '#2e7d32',
      cancelled: '#ed6c02',
    };
    const labels: Record<FollowupSegmentStatus, string> = {
      draft: 'Entwurf',
      awaiting_webinar: 'Wartet',
      scheduled: 'Geplant',
      sent: 'Gesendet',
      cancelled: 'Abgebr.',
    };

    const displayStatus = status || 'draft';
    const recipientText = recipients !== undefined ? ` (${recipients})` : '';

    return (
      <Tooltip title={`${label}: ${labels[displayStatus]}${recipientText} Empfänger`}>
        <Chip
          size="small"
          label={`${labels[displayStatus]}${recipientText}`}
          sx={{ backgroundColor: colors[displayStatus], color: 'white', minWidth: 80 }}
        />
      </Tooltip>
    );
  };

  // Determine if campaign can be deleted (only if both segments are draft, awaiting_webinar, or disabled)
  const canDelete = (campaign: FollowupCampaign): boolean => {
    const rnaCanDelete =
      campaign.rnaEnabled === false ||
      campaign.rnaStatus === 'draft' ||
      campaign.rnaStatus === 'awaiting_webinar';
    const anoCanDelete =
      campaign.anoEnabled === false ||
      campaign.anoStatus === 'draft' ||
      campaign.anoStatus === 'awaiting_webinar';
    return rnaCanDelete && anoCanDelete;
  };

  // Determine if campaign can be cancelled (has scheduled, sent, or awaiting_webinar segments)
  const canCancel = (campaign: FollowupCampaign): boolean => {
    const rnaCanCancel =
      campaign.rnaEnabled === true &&
      (campaign.rnaStatus === 'scheduled' ||
        campaign.rnaStatus === 'sent' ||
        campaign.rnaStatus === 'awaiting_webinar');
    const anoCanCancel =
      campaign.anoEnabled === true &&
      (campaign.anoStatus === 'scheduled' ||
        campaign.anoStatus === 'sent' ||
        campaign.anoStatus === 'awaiting_webinar');
    return rnaCanCancel || anoCanCancel;
  };

  // Determine if campaign can be rescheduled (only if cancelled AND no segment is 'sent')
  const canReschedule = (campaign: FollowupCampaign): boolean => {
    return (
      campaign.status === 'cancelled' &&
      campaign.rnaStatus !== 'sent' &&
      campaign.anoStatus !== 'sent'
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Nachfass-Kampagnen</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onNew}>
          Neue Nachfass-Kampagne
        </Button>
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                ID
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Name
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Webinar
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                <Tooltip title="Registriert, Nicht Anwesend">
                  <span>RNA</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                <Tooltip title="Anwesend, Nicht Gekauft">
                  <span>ANO</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Erstellt
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Keine Nachfass-Kampagnen gefunden
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>{campaign.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {campaign.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Parent: {campaign.parent_campaign_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {campaign.webinar_title || '-'}
                    </Typography>
                    {campaign.webinar_date && (
                      <Typography variant="caption" color="textSecondary">
                        {moment(campaign.webinar_date).format('DD.MM.YYYY')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {getSegmentStatusChip(
                      campaign.rnaStatus,
                      campaign.rnaEnabled,
                      campaign.rnaRecipients,
                      'RNA',
                    )}
                  </TableCell>
                  <TableCell>
                    {getSegmentStatusChip(
                      campaign.anoStatus,
                      campaign.anoEnabled,
                      campaign.anoRecipients,
                      'ANO',
                    )}
                  </TableCell>
                  <TableCell>{moment(campaign.created_at).format('DD.MM.YYYY')}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => onEdit(campaign.id)} title="Bearbeiten">
                      <EditIcon />
                    </IconButton>
                    {canCancel(campaign) && (
                      <IconButton
                        size="small"
                        onClick={() => handleCancelClick(campaign)}
                        title="Abbrechen"
                        color="warning"
                      >
                        <CancelIcon />
                      </IconButton>
                    )}
                    {canReschedule(campaign) && (
                      <IconButton
                        size="small"
                        onClick={() => handleReschedule(campaign.id)}
                        title="Zur Bearbeitung freigeben"
                        color="primary"
                        disabled={rescheduling === campaign.id}
                      >
                        <RefreshIcon />
                      </IconButton>
                    )}
                    {canDelete(campaign) && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(campaign.id)}
                        title="Löschen"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>Nachfass-Kampagne abbrechen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelTarget && (
              <>
                <strong>{cancelTarget.name}</strong>
                <br />
                <br />
                Alle geplanten E-Mails in Iterable werden storniert. Bereits gesendete E-Mails
                können nicht rückgängig gemacht werden.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} disabled={cancelling}>
            Zurück
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="warning"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? 'Wird abgebrochen...' : 'Abbrechen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FollowupCampaignList;
